import { Dialog, Transition } from '@headlessui/react'
import {
  ArrowsRightLeftIcon,
  MagnifyingGlassIcon,
  UserIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import { API, Auth } from 'aws-amplify'
import React, { Fragment, useEffect, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useAppContext } from '../libs/context'
import Spin from './Spin'

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const UserImpersonation = ({ collapsed = false }) => {
  const { userInfo, setUserInfo } = useAppContext()
  const history = useHistory()
  const [isOpen, setIsOpen] = useState(false)
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [users, setUsers] = useState([])
  const [filteredUsers, setFilteredUsers] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [error, setError] = useState(null)
  // Usar una ref para mantener el usuario seleccionado incluso si el estado se pierde
  const selectedUserRef = useRef(null)

  useEffect(() => {
    if (isOpen && userInfo?.isAdmin) {
      fetchUsers()
    } else if (!isOpen && !isConfirmOpen) {
      // Limpiar cuando se cierra el modal principal SOLO si el modal de confirmación también está cerrado
      setUsers([])
      setFilteredUsers([])
      setSearchTerm('')
      setError(null)
      // No limpiar selectedUser aquí para evitar problemas con el modal de confirmación
      // setSelectedUser(null)
    }
  }, [isOpen, isConfirmOpen])

  // Limpiar selectedUser solo cuando ambos modales están cerrados Y no hay impersonación activa
  // NO limpiar mientras el modal de confirmación esté abierto
  useEffect(() => {
    if (!isConfirmOpen && !isOpen && !userInfo?.impersonatedUserId) {
      setSelectedUser(null)
      selectedUserRef.current = null
    }
  }, [isConfirmOpen, isOpen])

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredUsers(users)
    } else {
      const filtered = users.filter(
        (user) =>
          user.userId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.username?.toLowerCase().includes(searchTerm.toLowerCase())
      )
      setFilteredUsers(filtered)
    }
  }, [searchTerm, users])

  const fetchUsers = async () => {
    setLoading(true)
    setError(null)
    setUsers([])
    setFilteredUsers([])
    try {
      const user = await Auth.currentAuthenticatedUser()
      const tokenReq = user.signInUserSession.idToken.jwtToken

      // Obtener información del usuario para debugging
      const session = await Auth.currentSession()
      const accessToken = session.getAccessToken()
      const groups = accessToken.payload['cognito:groups']
      const userId = user.signInUserSession.idToken.payload.sub
      const email = user.signInUserSession.idToken.payload.email

      // Amplify maneja automáticamente la autenticación, pero en local puede necesitar el formato Bearer
      const usersList = await API.get('rcs-api', '/users', {
        headers: {
          Authorization: `Bearer ${tokenReq}`
        }
      })

      if (usersList && Array.isArray(usersList)) {
        if (usersList.length > 0) {
          // Filtrar solo usuarios regulares (excluir admins si tienen esa propiedad)
          // y excluir el usuario actual que está logueado
          const regularUsers = usersList.filter((u) => {
            return (
              u.userId &&
              u.userId !== userId &&
              u.email !== email &&
              u.userId !== userInfo?.userId &&
              u.email !== userInfo?.email
            )
          })
          setUsers(regularUsers)
          setFilteredUsers(regularUsers)
        } else {
          console.warn('Users list is empty')
          setUsers([])
          setFilteredUsers([])
          setError('No hay usuarios en la base de datos')
        }
      } else {
        console.warn('Users list is not an array:', usersList)
        setUsers([])
        setFilteredUsers([])
        setError('Formato de respuesta inválido')
      }
    } catch (err) {
      console.error('Error fetching users:', err)
      console.error('Error details:', {
        message: err.message,
        response: err.response,
        code: err.code,
        responseData: err.response?.data
      })
      setUsers([])
      setFilteredUsers([])
      setError(
        err.message ||
          'Error al cargar usuarios. Verifica la consola para más detalles.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleUserClick = (user) => {
    // Guardar primero en la ref para asegurarnos de que no se pierda
    selectedUserRef.current = user
    setSelectedUser(user)
    setIsConfirmOpen(true)
  }

  const handleConfirmImpersonation = async (userParam) => {
    // Si se pasa un usuario como parámetro, usarlo; si no, usar el estado o la ref
    const userToImpersonate =
      userParam || selectedUser || selectedUserRef.current

    if (!userToImpersonate) {
      setError('No se ha seleccionado ningún usuario')
      setIsConfirmOpen(false)
      return
    }

    try {
      setLoading(true)

      // Guardar el usuario original antes de impersonar si no existe
      const originalUserInfo = userInfo.originalEmail
        ? userInfo
        : {
            ...userInfo,
            originalEmail: userInfo.email,
            originalUserId: userInfo.userId
          }

      // Obtener el token del usuario impersonado del backend
      const user = await Auth.currentAuthenticatedUser()
      const tokenResponse = await API.post('rcs-api', '/impersonation-token', {
        headers: {
          Authorization: user.signInUserSession.idToken.jwtToken
        },
        body: {
          userId: userToImpersonate.userId
        }
      })

      if (!tokenResponse.idToken) {
        throw new Error('Failed to get impersonation token')
      }

      // Guardar el token del usuario impersonado
      localStorage.setItem('impersonatedToken', tokenResponse.idToken)

      // Guardar en localStorage para persistencia
      localStorage.setItem('impersonatedUserId', userToImpersonate.userId)
      localStorage.setItem('impersonatedEmail', userToImpersonate.email || '')
      localStorage.setItem(
        'impersonatedUsername',
        userToImpersonate.username || ''
      )
      localStorage.setItem(
        'originalEmail',
        originalUserInfo.originalEmail || userInfo.email
      )
      localStorage.setItem(
        'originalUserId',
        originalUserInfo.originalUserId || userInfo.userId
      )

      // Cerrar modales primero
      setIsConfirmOpen(false)
      setIsOpen(false)

      // Actualizar el contexto con el usuario impersonado pero manteniendo isAdmin
      const newUserInfo = {
        ...originalUserInfo,
        impersonatedUserId: userToImpersonate.userId,
        impersonatedEmail: userToImpersonate.email,
        impersonatedUsername: userToImpersonate.username,
        // Mantener el perfil de admin
        isAdmin: true
      }

      setUserInfo(newUserInfo)

      // Limpiar estados
      setSelectedUser(null)
      setSearchTerm('')

      // Recargar la página actual para refrescar las campañas del usuario seleccionado
      window.location.reload()
    } catch (error) {
      console.error('Error al confirmar impersonación:', error)
      setError(
        'Error al cambiar de usuario: ' + (error.message || 'Error desconocido')
      )
      setIsConfirmOpen(false)
    }
  }

  const handleStopImpersonation = () => {
    const originalEmail =
      userInfo.originalEmail || localStorage.getItem('originalEmail')
    const originalUserId =
      userInfo.originalUserId || localStorage.getItem('originalUserId')

    if (originalEmail) {
      // Restaurar el usuario original
      setUserInfo({
        email: originalEmail,
        userId: originalUserId,
        isAdmin: true
      })
    } else {
      // Si no hay original, simplemente limpiar la impersonación
      const {
        impersonatedUserId,
        impersonatedEmail,
        impersonatedUsername,
        originalEmail,
        originalUserId,
        ...rest
      } = userInfo
      setUserInfo({
        ...rest
      })
    }

    // Limpiar localStorage incluyendo el token de impersonación
    localStorage.removeItem('impersonatedToken')
    localStorage.removeItem('impersonatedUserId')
    localStorage.removeItem('impersonatedEmail')
    localStorage.removeItem('impersonatedUsername')
    localStorage.removeItem('originalEmail')
    localStorage.removeItem('originalUserId')

    setIsOpen(false)
    setSearchTerm('')

    // Recargar la página
    window.location.reload()
  }

  if (!userInfo.isAdmin) {
    return null
  }

  return (
    <>
      {/* Botón compacto para cambiar de usuario - solo visible cuando no hay impersonación activa */}
      {!userInfo.impersonatedUserId && (
        <button
          onClick={() => {
            setIsOpen(true)
          }}
          className={classNames(
            'transition-colors rounded hover:bg-gray-600/50 focus:outline-none focus:ring-2 focus:ring-white',
            collapsed
              ? 'flex items-center justify-center w-10 h-10 text-gray-400 hover:text-yellow-300'
              : 'text-xs text-gray-400 hover:text-yellow-300 px-2 py-1'
          )}
          title="Cambiar de usuario"
        >
          {collapsed ? (
            <ArrowsRightLeftIcon className="h-5 w-5" aria-hidden="true" />
          ) : (
            'Cambiar usuario'
          )}
        </button>
      )}

      {/* Botón para dejar de impersonar - solo visible cuando hay impersonación activa */}
      {userInfo.impersonatedUserId && (
        <button
          onClick={handleStopImpersonation}
          className={classNames(
            'transition-all rounded-md bg-red-500/20 hover:bg-red-500/30 border border-red-500/50 hover:border-red-500/70 font-medium animate-pulse focus:outline-none focus:ring-2 focus:ring-red-500',
            collapsed
              ? 'flex items-center justify-center w-10 h-10 text-red-300 hover:text-red-100'
              : 'text-xs text-red-300 hover:text-red-100 px-2 py-1'
          )}
          title="Dejar de ver como este usuario"
        >
          {collapsed ? (
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          ) : (
            'Dejar de ver'
          )}
        </button>
      )}

      {/* Modal de lista de usuarios */}
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={setIsOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-2xl sm:p-6">
                  <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => setIsOpen(false)}
                    >
                      <span className="sr-only">Cerrar</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900 mb-4"
                      >
                        Seleccionar usuario
                      </Dialog.Title>

                      {/* Barra de búsqueda */}
                      <div className="relative mb-4">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <MagnifyingGlassIcon
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                        </div>
                        <input
                          type="text"
                          className="block w-full rounded-md border-0 py-1.5 pl-10 pr-3 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                          placeholder="Buscar por email, userId o nombre..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>

                      {/* Lista de usuarios */}
                      {loading ? (
                        <div className="flex flex-col items-center justify-center py-8">
                          <Spin type="lightBg" />
                          <p className="text-sm text-gray-500 mt-2">
                            Cargando usuarios...
                          </p>
                        </div>
                      ) : (
                        <div className="max-h-96 overflow-y-auto">
                          {filteredUsers.length === 0 ? (
                            <div className="text-center py-4">
                              <p className="text-gray-500">
                                {searchTerm
                                  ? 'No se encontraron usuarios'
                                  : 'No hay usuarios disponibles'}
                              </p>
                              {error && (
                                <p className="text-xs text-red-500 mt-2">
                                  {error}
                                </p>
                              )}
                              {!error && users.length === 0 && !loading && (
                                <p className="text-xs text-gray-400 mt-2">
                                  Verifica la consola del navegador para más
                                  detalles
                                </p>
                              )}
                            </div>
                          ) : (
                            <ul className="divide-y divide-gray-200">
                              {filteredUsers.map((user) => (
                                <li
                                  key={user.userId}
                                  className="py-3 px-3 hover:bg-gray-50 cursor-pointer rounded-md transition-colors"
                                  onClick={() => handleUserClick(user)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-900 truncate">
                                        {user.username || user.userId}
                                      </p>
                                      <p className="text-sm text-gray-500 truncate">
                                        {user.email || user.userId}
                                      </p>
                                    </div>
                                    <div className="ml-4 flex-shrink-0">
                                      <UserIcon
                                        className="h-5 w-5 text-gray-400"
                                        aria-hidden="true"
                                      />
                                    </div>
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}

                      {/* Botón para dejar de impersonar si está activo */}
                      {userInfo.impersonatedUserId && (
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <button
                            type="button"
                            className="w-full rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
                            onClick={handleStopImpersonation}
                          >
                            Dejar de ver como{' '}
                            {userInfo.impersonatedEmail?.split('@')[0] ||
                              userInfo.impersonatedUserId}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Modal de confirmación */}
      <Transition.Root show={isConfirmOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            // Solo cerrar el modal, no limpiar selectedUser aquí
            setIsConfirmOpen(false)
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="sm:flex sm:items-start">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                      <UserIcon
                        className="h-6 w-6 text-blue-600"
                        aria-hidden="true"
                      />
                    </div>
                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                      <Dialog.Title
                        as="h3"
                        className="text-base font-semibold leading-6 text-gray-900"
                      >
                        Cambiar de usuario
                      </Dialog.Title>
                      <div className="mt-2">
                        <p className="text-sm text-gray-500">
                          ¿Deseas cambiar de usuario y ver las campañas,
                          templates y datos de{' '}
                          <span className="font-semibold text-gray-900">
                            {(selectedUser || selectedUserRef.current)
                              ?.username ||
                              (selectedUser || selectedUserRef.current)
                                ?.email ||
                              (selectedUser || selectedUserRef.current)?.userId}
                          </span>
                          ?
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          Mantendrás tu perfil de administrador y podrás volver
                          a tu cuenta en cualquier momento.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      disabled={loading}
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        const userToUse =
                          selectedUser || selectedUserRef.current
                        // Pasar el usuario como parámetro para asegurarnos de que no se pierda
                        handleConfirmImpersonation(userToUse)
                      }}
                    >
                      {loading ? 'Cambiando...' : 'Aceptar'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                      onClick={() => {
                        setIsConfirmOpen(false)
                        setSelectedUser(null)
                      }}
                    >
                      Cancelar
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  )
}

export default UserImpersonation
